// Users list + create dialog.
// platform_admin sees every dealer + can create any role.
// dealer_owner sees only their dealer + can only create dealer_manager / salesperson.
import React, { useEffect, useMemo, useState } from 'react';
import { Users as UsersIcon, Plus, RefreshCw, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { usersApi, dealersApi } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { ROLE_LABELS } from '../../auth/permissions';

const ROLE_OPTIONS_BY_ACTOR = {
  platform_admin: ['platform_admin', 'dealer_owner', 'dealer_manager', 'salesperson'],
  dealer_owner: ['dealer_manager', 'salesperson'],
};

const emptyForm = (defaults = {}) => ({
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  role: 'salesperson',
  dealer_id: '',
  ...defaults,
});

const UsersCard = () => {
  const { user: actor } = useAuth();
  const isAdmin = actor?.role === 'platform_admin';

  const [users, setUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Edit dialog state
  const [editTarget, setEditTarget] = useState(null);   // the user row being edited
  const [editForm, setEditForm] = useState({ role: '', status: '', first_name: '', last_name: '' });
  const [editSaving, setEditSaving] = useState(false);

  const roleOptions = useMemo(
    () => ROLE_OPTIONS_BY_ACTOR[actor?.role] || [],
    [actor?.role]
  );

  const refresh = async () => {
    setLoading(true);
    try {
      const [u, d] = await Promise.all([
        usersApi.list(),
        isAdmin ? dealersApi.list() : Promise.resolve([]),
      ]);
      setUsers(u);
      setDealers(d);
    } catch (err) {
      toast.error(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [actor?.role]);

  const openCreate = () => {
    setForm(emptyForm({
      role: roleOptions[0] || 'salesperson',
      dealer_id: isAdmin ? '' : actor.dealer_id || '',
    }));
    setOpen(true);
  };

  const handleCreate = async () => {
    if (!form.email || !form.password) {
      toast.error('Email and password are required');
      return;
    }
    const payload = {
      email: form.email.trim().toLowerCase(),
      password: form.password,
      first_name: form.first_name.trim() || undefined,
      last_name: form.last_name.trim() || undefined,
      role: form.role,
    };
    if (isAdmin) {
      // Platform admin must pass dealer_id for non-platform_admin roles.
      if (form.role !== 'platform_admin') {
        if (!form.dealer_id) {
          toast.error('Select a dealer for this user');
          return;
        }
        payload.dealer_id = form.dealer_id;
      }
    }
    // dealer_owner: backend pins dealer_id to actor's dealer.

    setSaving(true);
    try {
      await usersApi.create(payload);
      toast.success(`Created ${payload.email}`);
      setOpen(false);
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const dealerName = (id) => dealers.find((d) => d.id === id)?.name || id || '—';

  // --- Edit dialog ---------------------------------------------------------
  // platform_admin can edit any non-self user; dealer_owner can only edit
  // users in their own dealer whose current role is dealer_manager or
  // salesperson (backend enforces; this only hides the button early).
  const canEdit = (u) => {
    if (!actor || !u || u.id === actor.id) return false;
    if (actor.role === 'platform_admin') return u.role !== 'platform_admin' || u.id !== actor.id;
    if (actor.role === 'dealer_owner') {
      return u.dealer_id === actor.dealer_id
        && ['dealer_manager', 'salesperson'].includes(u.role);
    }
    return false;
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      role: u.role,
      status: u.status,
      first_name: u.first_name || '',
      last_name: u.last_name || '',
    });
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      // Build a minimal patch so the backend only touches what changed.
      const patch = {};
      if (editForm.role !== editTarget.role) patch.role = editForm.role;
      if (editForm.status !== editTarget.status) patch.status = editForm.status;
      if ((editForm.first_name || '') !== (editTarget.first_name || '')) patch.first_name = editForm.first_name;
      if ((editForm.last_name || '') !== (editTarget.last_name || '')) patch.last_name = editForm.last_name;
      if (Object.keys(patch).length === 0) {
        setEditTarget(null);
        return;
      }
      await usersApi.update(editTarget.id, patch);
      toast.success('User updated');
      setEditTarget(null);
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };

  // Role options shown in the edit dialog — dealer_owner cannot promote to
  // dealer_owner or platform_admin (backend also enforces).
  const editableRoleOptions = useMemo(() => {
    if (!editTarget) return [];
    if (actor?.role === 'platform_admin') {
      return ['dealer_owner', 'dealer_manager', 'salesperson'];
    }
    return ['dealer_manager', 'salesperson'];
  }, [actor?.role, editTarget]);

  return (
    <Card className="glass-card border-white/20" data-testid="users-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <UsersIcon className="w-5 h-5 text-primary" />
          <div>
            <CardTitle>Users</CardTitle>
            <CardDescription className="mt-1">
              {isAdmin ? 'All users across every dealer' : 'Users in your dealership'}
            </CardDescription>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={refresh} disabled={loading} data-testid="users-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2" onClick={openCreate} data-testid="users-create-btn">
                <Plus className="w-4 h-4" /> New User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>First name</Label>
                    <Input
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last name</Label>
                    <Input
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Temporary password</Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="user-password-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    8+ chars, 1 uppercase, 1 digit, 1 special.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((r) => (
                        <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isAdmin && form.role !== 'platform_admin' && (
                  <div className="space-y-2">
                    <Label>Dealer</Label>
                    <Select
                      value={form.dealer_id}
                      onValueChange={(v) => setForm({ ...form, dealer_id: v })}
                    >
                      <SelectTrigger data-testid="user-dealer-select">
                        <SelectValue placeholder="Select a dealer…" />
                      </SelectTrigger>
                      <SelectContent>
                        {dealers.map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
                <Button onClick={handleCreate} disabled={saving} data-testid="user-save-btn">
                  {saving ? 'Saving…' : 'Create user'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : users.length === 0 ? (
          <div className="text-sm text-muted-foreground">No users yet.</div>
        ) : (
          <div className="divide-y divide-border rounded-md border">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`user-row-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {u.email} · {ROLE_LABELS[u.role] || u.role}
                    {isAdmin && u.dealer_id ? ` · ${dealerName(u.dealer_id)}` : ''}
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                  u.status === 'active' ? 'bg-green-500/10 text-green-600' :
                  u.status === 'disabled' ? 'bg-red-500/10 text-red-600' :
                  'bg-muted text-muted-foreground'
                }`}>{u.status}</span>
                {canEdit(u) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(u)}
                    data-testid={`user-edit-${u.id}`}
                    aria-label={`Edit ${u.email}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Edit dialog — role / status / name */}
      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-4 py-2">
              <div className="text-sm text-muted-foreground">
                {editTarget.email}
                {editTarget.dealer_id ? ` · ${dealerName(editTarget.dealer_id)}` : ''}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>First name</Label>
                  <Input
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last name</Label>
                  <Input
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                >
                  <SelectTrigger data-testid="user-edit-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {editableRoleOptions.map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                >
                  <SelectTrigger data-testid="user-edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                    <SelectItem value="invited">Invited</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={editSaving} data-testid="user-edit-save">
              {editSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UsersCard;
